// iter-121.118 — EA Cockpit Scope Bar
//
// Sticky bar above the EA Home page. Shows:
//   • Tenant chip (pinned)
//   • Personalised greeting + dynamic status subtitle from /home/ea/header
//   • Period selector (24h/7d/30d/90d/MTD/QTD/YTD/Custom)
//   • Cascading filters: Workspace → Dataset, plus User
//   • "Save as default"
// NO trial pill — per user direction 3c, EAs are operators not billing owners.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Bookmark, Calendar, Database, Folder, User, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useEaScope } from "@/lib/ea-scope";

const PERIOD_OPTIONS = [
    { value: "7d",  label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "mtd", label: "Month to date" },
    { value: "qtd", label: "Quarter to date" },
    { value: "ytd", label: "Year to date" },
    { value: "custom", label: "Custom range…" },
];

const SENTINEL_ALL = "__all__";

export default function EaScopeBar({ tenantName, dynamicSubtitle, userName }) {
    const {
        scope, options, optionsLoading, saving,
        setPeriod, setCustomRange,
        setWorkspaceId, setOwnerId, setDatasetId,
        saveAsDefault,
    } = useEaScope();

    const onSave = async () => {
        const ok = await saveAsDefault();
        if (ok) toast.success("Default view saved");
        else toast.error("Failed to save default view");
    };

    const customLabel = (() => {
        if (scope.period !== "custom") return "Custom range";
        const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "?";
        return `${fmt(scope.custom_start)} → ${fmt(scope.custom_end)}`;
    })();

    const greeting = (() => {
        const h = new Date().getHours();
        const tod = h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
        return userName ? `Good ${tod}, ${userName}` : "Welcome back";
    })();

    return (
        <div className="border border-border rounded-sm bg-card p-3 mb-4"
             data-testid="ea-scope-bar">
            {/* Top row: greeting + tenant chip + dynamic status subtitle */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between mb-3">
                <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                        <div className="text-sm font-medium truncate" data-testid="ea-greeting">
                            {greeting}
                        </div>
                        {tenantName && (
                            <div className="inline-flex items-center gap-1.5 px-2 h-6 rounded-sm border border-border bg-secondary/40 text-[11px]"
                                 data-testid="ea-tenant-chip">
                                <Building2 className="h-3 w-3" />
                                <span className="truncate max-w-[200px]">{tenantName}</span>
                            </div>
                        )}
                    </div>
                    {dynamicSubtitle && (
                        <div className="text-xs text-muted-foreground" data-testid="ea-status-subtitle">
                            {dynamicSubtitle}
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom row: scope controls */}
            <div className="flex flex-wrap items-center gap-2">
                <Select value={scope.period} onValueChange={setPeriod}>
                    <SelectTrigger className="h-8 w-[150px] rounded-sm text-[12px]" data-testid="ea-period-select">
                        <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="Period" />
                    </SelectTrigger>
                    <SelectContent>
                        {PERIOD_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value} data-testid={`ea-period-${o.value}`}>
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {scope.period === "custom" && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm"
                                    className="h-8 rounded-sm text-[12px] font-normal"
                                    data-testid="ea-custom-range-trigger">
                                <Calendar className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                                {customLabel}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-auto p-0"
                                        data-testid="ea-custom-range-popover">
                            <CalendarPicker
                                mode="range"
                                selected={{
                                    from: scope.custom_start ? new Date(scope.custom_start) : undefined,
                                    to:   scope.custom_end   ? new Date(scope.custom_end)   : undefined,
                                }}
                                onSelect={(range) => {
                                    if (range?.from) {
                                        setCustomRange(
                                            range.from.toISOString().slice(0, 10),
                                            range.to ? range.to.toISOString().slice(0, 10) : range.from.toISOString().slice(0, 10),
                                        );
                                    }
                                }}
                                numberOfMonths={2}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                )}

                <Select
                    value={scope.workspace_id || SENTINEL_ALL}
                    onValueChange={(v) => setWorkspaceId(v === SENTINEL_ALL ? null : v)}
                    disabled={optionsLoading || options.workspaces.length === 0}
                >
                    <SelectTrigger className="h-8 w-[180px] rounded-sm text-[12px]" data-testid="ea-workspace-select">
                        <Folder className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="All workspaces" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={SENTINEL_ALL} data-testid="ea-workspace-all">All workspaces</SelectItem>
                        {options.workspaces.map((w) => (
                            <SelectItem key={w.id} value={w.id} data-testid={`ea-workspace-${w.id}`}>
                                {w.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select
                    value={scope.owner_id || SENTINEL_ALL}
                    onValueChange={(v) => setOwnerId(v === SENTINEL_ALL ? null : v)}
                    disabled={optionsLoading || options.owners.length === 0}
                >
                    <SelectTrigger className="h-8 w-[170px] rounded-sm text-[12px]" data-testid="ea-owner-select">
                        <User className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="All users" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={SENTINEL_ALL} data-testid="ea-owner-all">All users</SelectItem>
                        {options.owners.map((u) => (
                            <SelectItem key={u.id} value={u.id} data-testid={`ea-owner-${u.id}`}>
                                {u.name || u.email}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select
                    value={scope.dataset_id || SENTINEL_ALL}
                    onValueChange={(v) => setDatasetId(v === SENTINEL_ALL ? null : v)}
                    disabled={optionsLoading || options.datasets.length === 0}
                >
                    <SelectTrigger className="h-8 w-[170px] rounded-sm text-[12px]" data-testid="ea-dataset-select">
                        <Database className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        <SelectValue placeholder="All datasets" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={SENTINEL_ALL} data-testid="ea-dataset-all">All datasets</SelectItem>
                        {options.datasets.slice(0, 200).map((d) => (
                            <SelectItem key={d.id} value={d.id} data-testid={`ea-dataset-${d.id}`}>
                                {d.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Button size="sm" variant="outline" className="rounded-sm h-8"
                        onClick={onSave} disabled={saving}
                        data-testid="ea-save-default">
                    <Bookmark className="h-3.5 w-3.5 mr-1.5" />
                    {saving ? "Saving…" : "Save as default"}
                </Button>
            </div>
        </div>
    );
}

// Convenience hook to fetch the EA header once and share it.
export function useEaHeader() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        api.get("/home/ea/header")
            .then((r) => { if (alive) setData(r.data || null); })
            .catch(() => { if (alive) setData(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);
    return { data, loading };
}
